import { useEffect, useMemo, useRef, useState } from "react";

import {
  type DocsShareFeedbackSeverity,
  formatDateTime,
  parseExpiryDaysInput,
  toShareUrl,
} from "@/pages/docsShareLinks/docsShareLinksUtils";
import {
  useCreateAdminDocsShareLinkMutation,
  useGetAdminDocsSearchQuery,
  useGetAdminDocsShareLinksQuery,
  useRevokeAdminDocsShareLinkMutation,
} from "@/store/api/baseApi";
import type {
  AdminDocsShareLink,
  DocsShareStatusFilter,
} from "@/types/docsShareLinks";
import { getErrorMessage, getPublicErrorDetails } from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextDialog,
  VNextKeyValueList,
  VNextNotice,
} from "@/vnext/primitives";
import { DocsSearchCard } from "@/vnext/routes/settings/docs-share-links/DocsSearchCard";
import { DocsShareLinksTable } from "@/vnext/routes/settings/docs-share-links/DocsShareLinksTable";
import { GeneratedShareUrlCard } from "@/vnext/routes/settings/docs-share-links/GeneratedShareUrlCard";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";

import "@/vnext/routes/settings/docs-share-links/docsShareLinksMake.css";

const SEARCH_LIMIT = 50;
const SHARE_LIST_LIMIT = 50;
const logger = FrontendLoggerFactory.getLogger().withComponent(
  "DocsShareLinksRoute",
);

interface GeneratedShareLinkState {
  shareUrl: string;
  docPath: string;
  expiresAt: string;
}

type CreateShareLinkSource = "search-result" | "existing-link";

interface PendingCreateShareLinkAction {
  kind: "create";
  docPath: string;
  expiresInDays: number | null;
  source: CreateShareLinkSource;
}

interface PendingDisableShareLinkAction {
  kind: "disable";
  link: AdminDocsShareLink;
}

type PendingShareLinkAction =
  PendingCreateShareLinkAction | PendingDisableShareLinkAction;

const formatExpiryDays = (days: number | null): string =>
  days === null ? "Default expiry" : `${days} ${days === 1 ? "day" : "days"}`;

export default function DocsShareLinksRoute() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number | "">(30);
  const [statusFilter, setStatusFilter] =
    useState<DocsShareStatusFilter>("all");
  const [generatedShareLink, setGeneratedShareLink] =
    useState<GeneratedShareLinkState | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackSeverity, setFeedbackSeverity] =
    useState<DocsShareFeedbackSeverity>("info");
  const [pendingShareLinkAction, setPendingShareLinkAction] =
    useState<PendingShareLinkAction | null>(null);
  const [isConfirmingShareLinkAction, setIsConfirmingShareLinkAction] =
    useState(false);
  const shareLinkConfirmationInFlightRef = useRef(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchInput]);

  const {
    data: searchResults,
    isFetching: isSearching,
    error: searchError,
  } = useGetAdminDocsSearchQuery(
    {
      query: debouncedSearch,
      limit: SEARCH_LIMIT,
      includeArchived,
    },
    {
      skip: debouncedSearch.length < 2,
    },
  );
  const {
    data: shareLinksResponse,
    isFetching: isShareLinksFetching,
    error: shareLinksError,
    refetch: refetchShareLinks,
  } = useGetAdminDocsShareLinksQuery({
    status: statusFilter,
    limit: SHARE_LIST_LIMIT,
  });
  const [createShareLink, { isLoading: isCreatingShareLink }] =
    useCreateAdminDocsShareLinkMutation();
  const [revokeShareLink, { isLoading: isRevokingShareLink }] =
    useRevokeAdminDocsShareLinkMutation();

  const shareLinks = shareLinksResponse?.links ?? [];
  const matchedPaths = useMemo(
    () => searchResults?.paths ?? [],
    [searchResults?.paths],
  );
  const searchErrorMessage = useMemo(() => {
    if (!searchError) {
      return null;
    }

    return getErrorMessage(searchError, "Failed to search docs.");
  }, [searchError]);
  const shareLinksErrorMessage = useMemo(() => {
    if (!shareLinksError) {
      return null;
    }

    return getErrorMessage(shareLinksError, "Failed to load share links.");
  }, [shareLinksError]);

  useEffect(() => {
    if (!searchError) {
      return;
    }

    const details = getPublicErrorDetails(
      searchError,
      "Failed to search docs.",
    );
    logger.warn("Docs search failed", {
      operation: "docs_search_failed",
      query: debouncedSearch || undefined,
      includeArchived,
      errorCode: details.errorCode,
      correlationId: details.correlationId ?? undefined,
    });
  }, [debouncedSearch, includeArchived, searchError]);

  useEffect(() => {
    if (!shareLinksError) {
      return;
    }

    const details = getPublicErrorDetails(
      shareLinksError,
      "Failed to load share links.",
    );
    logger.warn("Docs share links load failed", {
      operation: "docs_share_links_load_failed",
      errorCode: details.errorCode,
      correlationId: details.correlationId ?? undefined,
    });
  }, [shareLinksError]);

  const showFeedback = (
    severity: DocsShareFeedbackSeverity,
    message: string,
  ) => {
    setFeedbackSeverity(severity);
    setFeedbackMessage(message);
  };

  const clearMutationFeedback = () => {
    setGeneratedShareLink(null);
    setFeedbackMessage(null);
  };

  const openCreateShareLinkConfirmation = (
    docPath: string,
    source: CreateShareLinkSource,
  ) => {
    const expiry = parseExpiryDaysInput(expiresInDays);
    clearMutationFeedback();
    setPendingShareLinkAction({
      kind: "create",
      docPath,
      expiresInDays: expiry,
      source,
    });
  };

  const handleConfirmCreateShareLink = async (): Promise<void> => {
    const pendingAction = pendingShareLinkAction;
    if (
      pendingAction?.kind !== "create" ||
      shareLinkConfirmationInFlightRef.current
    ) {
      return;
    }

    shareLinkConfirmationInFlightRef.current = true;
    setIsConfirmingShareLinkAction(true);

    try {
      const created = await createShareLink({
        docPath: pendingAction.docPath,
        expiresInDays: pendingAction.expiresInDays,
      }).unwrap();
      await refetchShareLinks();

      const shareUrl = toShareUrl(created.token);
      setPendingShareLinkAction(null);
      setGeneratedShareLink({
        shareUrl,
        docPath: created.docPath,
        expiresAt: created.expiresAt,
      });
      showFeedback("success", `Share link created for ${created.docPath}`);
    } catch (error) {
      const details = getPublicErrorDetails(
        error,
        "Failed to create share link.",
      );
      logger.warn("Docs share link creation failed", {
        operation: "docs_share_links_create_failed",
        errorCode: details.errorCode,
        correlationId: details.correlationId ?? undefined,
      });
      showFeedback(
        "error",
        getErrorMessage(error, "Failed to create share link."),
      );
      setPendingShareLinkAction(null);
    } finally {
      shareLinkConfirmationInFlightRef.current = false;
      setIsConfirmingShareLinkAction(false);
    }
  };

  const openDisableShareLinkConfirmation = (link: AdminDocsShareLink) => {
    clearMutationFeedback();
    setPendingShareLinkAction({
      kind: "disable",
      link,
    });
  };

  const handleConfirmDisableShareLink = async (): Promise<void> => {
    const pendingAction = pendingShareLinkAction;
    if (
      pendingAction?.kind !== "disable" ||
      shareLinkConfirmationInFlightRef.current
    ) {
      return;
    }

    shareLinkConfirmationInFlightRef.current = true;
    setIsConfirmingShareLinkAction(true);

    try {
      await revokeShareLink({ id: pendingAction.link.id }).unwrap();
      await refetchShareLinks();
      setPendingShareLinkAction(null);
      showFeedback("success", "Share link revoked.");
    } catch (error) {
      const details = getPublicErrorDetails(
        error,
        "Failed to revoke share link.",
      );
      logger.warn("Docs share link revocation failed", {
        operation: "docs_share_links_revoke_failed",
        shareLinkId: pendingAction.link.id,
        errorCode: details.errorCode,
        correlationId: details.correlationId ?? undefined,
      });
      showFeedback(
        "error",
        getErrorMessage(error, "Failed to revoke share link."),
      );
      setPendingShareLinkAction(null);
    } finally {
      shareLinkConfirmationInFlightRef.current = false;
      setIsConfirmingShareLinkAction(false);
    }
  };

  const closePendingShareLinkAction = () => {
    if (shareLinkConfirmationInFlightRef.current) {
      return;
    }

    setPendingShareLinkAction(null);
  };

  const handleCopy = async (value: string): Promise<void> => {
    // DOM typings model clipboard as always present, but older or locked-down
    // browsers can still omit it at runtime.
    const clipboard = (
      navigator as Omit<Navigator, "clipboard"> & {
        clipboard?: Partial<Clipboard>;
      }
    ).clipboard;

    if (typeof clipboard?.writeText !== "function") {
      logger.warn("Docs share link copy unavailable", {
        operation: "docs_share_links_copy_unavailable",
      });
      showFeedback(
        "error",
        "Clipboard is unavailable in this browser. Use Open link or switch to a browser with clipboard support.",
      );
      return;
    }

    try {
      await clipboard.writeText(value);
      showFeedback("success", "Copied share URL.");
    } catch (error) {
      const details = getPublicErrorDetails(error, "Failed to copy share URL.");
      logger.warn("Docs share link copy failed", {
        operation: "docs_share_links_copy_failed",
        errorCode: details.errorCode,
        correlationId: details.correlationId ?? undefined,
      });
      showFeedback(
        "error",
        "Copy failed in this browser. Use Open link or retry in a browser with clipboard support.",
      );
    }
  };

  const handleRefreshShareLinks = async (): Promise<void> => {
    await refetchShareLinks();
  };

  return (
    <SettingsWorkspaceChrome
      subtitle="Generate short-lived public links for internal docs without exposing the wider admin site."
      title="Document share links"
    >
      <div className="docs-share-links-route">
        <VNextNotice tone="warning">
          <p className="docs-share-links-route__intro">
            <strong>Public-share policy</strong>
          </p>
          <p className="docs-share-links-route__intro">
            Docs share links are public. Use the shortest practical expiry,
            revoke links when work is complete, and request approval before
            broad external distribution.
          </p>
        </VNextNotice>

        {feedbackMessage ? (
          <VNextNotice tone={feedbackSeverity === "error" ? "danger" : "info"}>
            {feedbackMessage}
          </VNextNotice>
        ) : null}

        <DocsSearchCard
          debouncedSearch={debouncedSearch}
          expiresInDays={expiresInDays}
          includeArchived={includeArchived}
          isCreatingShareLink={isCreatingShareLink}
          isSearching={isSearching}
          matchedPaths={matchedPaths}
          onExpiresInDaysChange={setExpiresInDays}
          onGenerateShareLink={(docPath) => {
            openCreateShareLinkConfirmation(docPath, "search-result");
          }}
          onIncludeArchivedChange={setIncludeArchived}
          onSearchInputChange={setSearchInput}
          searchErrorMessage={searchErrorMessage}
          searchInput={searchInput}
        />

        {generatedShareLink ? (
          <GeneratedShareUrlCard
            generatedShareLink={generatedShareLink}
            onCopy={handleCopy}
          />
        ) : null}

        <DocsShareLinksTable
          isCreatingShareLink={isCreatingShareLink}
          isRevokingShareLink={isRevokingShareLink}
          isShareLinksFetching={isShareLinksFetching}
          onRefreshShareLinks={handleRefreshShareLinks}
          onRegenerateShareLink={(link) => {
            openCreateShareLinkConfirmation(link.docPath, "existing-link");
          }}
          onRevokeShareLink={openDisableShareLinkConfirmation}
          onStatusFilterChange={setStatusFilter}
          shareLinks={shareLinks}
          shareLinksErrorMessage={shareLinksErrorMessage}
          statusFilter={statusFilter}
        />

        {pendingShareLinkAction?.kind === "create" ? (
          <VNextDialog
            description="Review the document and expiry before creating a public link."
            footer={
              <>
                <VNextButton
                  disabled={isCreatingShareLink || isConfirmingShareLinkAction}
                  onClick={closePendingShareLinkAction}
                  tone="secondary"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={isCreatingShareLink || isConfirmingShareLinkAction}
                  onClick={() => {
                    void handleConfirmCreateShareLink();
                  }}
                  tone="primary"
                >
                  {isCreatingShareLink || isConfirmingShareLinkAction
                    ? "Creating link..."
                    : "Create link"}
                </VNextButton>
              </>
            }
            onClose={closePendingShareLinkAction}
            open
            title="Create public docs link"
          >
            <div className="vnext-stack">
              <p className="docs-share-links-route__intro">
                Anyone with this link can open the selected doc until the link
                expires.
              </p>
              {pendingShareLinkAction.source === "existing-link" ? (
                <VNextNotice tone="warning">
                  This creates an additional public link. Existing links stay
                  unchanged.
                </VNextNotice>
              ) : null}
              <VNextKeyValueList
                items={[
                  {
                    label: "Doc path",
                    value: pendingShareLinkAction.docPath,
                  },
                  {
                    label: "Expiry",
                    value: formatExpiryDays(
                      pendingShareLinkAction.expiresInDays,
                    ),
                  },
                  {
                    label: "Approval",
                    value:
                      "Check approval before sharing this link outside HelloCashflow.",
                  },
                ]}
              />
            </div>
          </VNextDialog>
        ) : null}

        {pendingShareLinkAction?.kind === "disable" ? (
          <VNextDialog
            description="Confirm before this public link stops working."
            footer={
              <>
                <VNextButton
                  disabled={isRevokingShareLink || isConfirmingShareLinkAction}
                  onClick={closePendingShareLinkAction}
                  tone="secondary"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={isRevokingShareLink || isConfirmingShareLinkAction}
                  onClick={() => {
                    void handleConfirmDisableShareLink();
                  }}
                  tone="primary"
                >
                  {isRevokingShareLink || isConfirmingShareLinkAction
                    ? "Disabling..."
                    : "Disable link"}
                </VNextButton>
              </>
            }
            onClose={closePendingShareLinkAction}
            open
            title="Disable public docs link"
          >
            <div className="vnext-stack">
              <p className="docs-share-links-route__intro">
                Disable this link when access is no longer approved. The
                selected public URL will stop working.
              </p>
              <VNextKeyValueList
                items={[
                  {
                    label: "Doc path",
                    value: pendingShareLinkAction.link.docPath,
                  },
                  {
                    label: "Status",
                    value: pendingShareLinkAction.link.status,
                  },
                  {
                    label: "Expires",
                    value: formatDateTime(
                      pendingShareLinkAction.link.expiresAt,
                    ),
                  },
                ]}
              />
            </div>
          </VNextDialog>
        ) : null}
      </div>
    </SettingsWorkspaceChrome>
  );
}
